
import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../../domain/services/searchService';
import { catalogService } from '../../domain/services/catalogService';
import { useContent } from '../../state/ContentContext';
import { Product } from '../../domain/types';
import { ProductCard } from '../../components/ProductCard';
import { typedStorage } from '../../utils/storage';

interface SearchOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    mode?: 'products' | 'blog';
}

export const SearchOverlay = ({ isOpen, onClose, mode = 'products' }: SearchOverlayProps) => {
    const navigate = useNavigate();
    const { posts } = useContent();
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    
    const [productResults, setProductResults] = useState<Product[]>([]);
    const [recentSearches, setRecentSearches] = useState<string[]>(() => {
        return typedStorage.searchHistory.get();
    });

    const [suggestedItems, setSuggestedItems] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);

    // 1. Debounce Logic
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedQuery(query);
        }, 400); // 400ms delay to save API calls

        return () => clearTimeout(handler);
    }, [query]);

    // 2. Load Suggestions Once
    useEffect(() => {
        if (isOpen && mode === 'products' && suggestedItems.length === 0) {
            catalogService.getFeaturedProducts().then(products => {
                setSuggestedItems(products.slice(0, 4));
            });
        }
    }, [isOpen, mode]);

    // 3. Filter Blogs (Hybrid: Client side filter from context or Server side via Service)
    const blogResults = useMemo(() => {
        if (mode !== 'blog' || debouncedQuery.length < 2) return [];
        const lowerQ = debouncedQuery.toLowerCase();
        // Uses context data for instant feedback, could call searchService.searchBlogPosts() for server-side
        return posts.filter(p => 
            p.title.toLowerCase().includes(lowerQ) || 
            p.content.toLowerCase().includes(lowerQ) ||
            p.tags.some(t => t.toLowerCase().includes(lowerQ))
        ).slice(0, 10);
    }, [debouncedQuery, posts, mode]);

    // 4. Server-Side Search Execution
    useEffect(() => {
        if (mode === 'products' && debouncedQuery.length > 1) {
            setLoading(true);
            searchService.searchProducts(debouncedQuery).then(results => {
                setProductResults(results);
                setLoading(false);
            });
        } else {
            setProductResults([]);
        }
    }, [debouncedQuery, mode]);

    const saveSearch = (term: string) => {
        if (!term.trim()) return;
        const newHistory = [term, ...recentSearches.filter(t => t !== term)].slice(0, 6);
        setRecentSearches(newHistory);
        typedStorage.searchHistory.set(newHistory);
    };

    const clearHistory = () => {
        setRecentSearches([]);
        typedStorage.searchHistory.remove();
    }

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[500] flex justify-center bg-black/20 dark:bg-black/40 backdrop-blur-3xl animate-fade-in overflow-hidden">
            <div className="w-full h-full md:max-w-xl lg:max-w-2xl bg-white/70 dark:bg-[#0c0a09]/80 flex flex-col shadow-2xl border-x border-black/5 dark:border-white/5">
                
                <div className="flex items-center gap-2 p-4 pt-safe border-b border-black/5 dark:border-white/5 bg-transparent shrink-0">
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/10 shrink-0 transition-colors">
                        <span className="material-symbols-outlined text-text-main dark:text-white">arrow_back</span>
                    </button>
                    <div className="flex-1 relative">
                        <input 
                            autoFocus
                            type="text" 
                            placeholder={mode === 'blog' ? "¿Qué historia buscas?" : "Buscar en AllTuc..."} 
                            className="w-full bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl py-3.5 pl-11 pr-4 text-text-main dark:text-white placeholder-text-secondary dark:placeholder-gray-500 focus:ring-2 focus:ring-primary/50 text-base font-bold transition-all outline-none shadow-inner"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && saveSearch(query)}
                        />
                        <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                            {mode === 'blog' ? 'menu_book' : 'search'}
                        </span>
                        {query && (
                            <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-text-main transition-colors">
                                <span className="material-symbols-outlined text-[20px] filled">cancel</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 no-scrollbar pb-40 bg-transparent">
                    {query.length > 0 ? (
                        <div className="space-y-6 animate-fade-in">
                            {mode === 'blog' ? (
                                <div className="space-y-4">
                                    <p className="text-[10px] font-black uppercase text-text-secondary dark:text-gray-400 tracking-widest pl-1">Historias AllTuc ({blogResults.length})</p>
                                    {blogResults.map(post => (
                                        <div 
                                            key={post.id} 
                                            onClick={() => { saveSearch(query); onClose(); navigate(`/blog/${post.id}`); }}
                                            className="bg-white/40 dark:bg-white/5 backdrop-blur-md p-4 rounded-3xl border border-black/5 dark:border-white/10 flex gap-4 items-center cursor-pointer active:scale-[0.98] transition-all shadow-sm hover:border-primary/30"
                                        >
                                            <div className="w-16 h-16 rounded-2xl bg-cover bg-center shrink-0 shadow-inner border border-black/5" style={{backgroundImage: `url("${post.imageUrl}")`}}></div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-sm text-text-main dark:text-white truncate">{post.title}</h4>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[9px] font-black uppercase text-primary bg-primary/10 px-2 py-0.5 rounded-lg border border-primary/10">{post.tags[0]}</span>
                                                    <span className="text-[9px] text-gray-400 flex items-center gap-1 font-mono"><span className="material-symbols-outlined text-[10px]">schedule</span> {post.readTime || '5 min'}</span>
                                                </div>
                                            </div>
                                            <span className="material-symbols-outlined text-gray-300 dark:text-gray-600">chevron_right</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-[10px] font-black uppercase text-text-secondary dark:text-gray-400 tracking-widest pl-1 flex items-center gap-2">
                                        Resultados Catálogo
                                        {loading && <span className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>}
                                    </p>
                                    <div className="grid grid-cols-2 gap-4">
                                        {productResults.map(p => (
                                            <div key={p.id} onClick={() => { saveSearch(query); onClose(); }} className="animate-fade-in">
                                                <ProductCard product={p} />
                                            </div>
                                        ))}
                                    </div>
                                    {!loading && productResults.length === 0 && (
                                        <div className="text-center py-10 text-gray-400">
                                            <p>No encontramos productos.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-10 animate-slide-up pb-20">
                            {recentSearches.length > 0 && (
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center px-1">
                                        <h3 className="font-black text-[10px] text-text-secondary dark:text-gray-400 uppercase tracking-widest">Búsquedas Recientes</h3>
                                        <button onClick={clearHistory} className="text-[10px] font-black text-primary uppercase hover:underline">Limpiar</button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {recentSearches.map((term, i) => (
                                            <button key={i} onClick={() => setQuery(term)} className="px-4 py-2 bg-white/40 dark:bg-white/5 backdrop-blur-md border border-black/5 dark:border-white/10 rounded-xl text-sm font-bold text-text-main dark:text-gray-300 flex items-center gap-2 active:scale-95 transition-all shadow-sm">
                                                <span className="material-symbols-outlined text-[18px] opacity-40">history</span>
                                                {term}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {mode === 'products' && (
                                <div className="space-y-4">
                                    <h3 className="font-black text-[10px] text-text-secondary dark:text-gray-400 uppercase tracking-widest px-1">Sugerencias para vos</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        {suggestedItems.map(p => (
                                            <div key={p.id} onClick={onClose} className="animate-fade-in">
                                                <ProductCard product={p} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};
